const SIGNED_IN_HINT_STORAGE_KEY = 'mcv.signedInHint';
const PROVIDER_NAME_HINT_STORAGE_KEY = 'mcv.providerNameHint';

/**
 * A one-bit "this browser was last seen signed in" marker. [MCV-1723]
 *
 * This is NOT authentication and nothing may trust it for access. It only
 * decides whether a COLD public page on the primary host boots Clerk (see
 * ApplicationBoundary): anonymous visitors keep the Clerk-free page, while a
 * browser that was last seen signed in boots Clerk so the public header can say
 * who is signed in instead of "Log in". Clerk remains the source of truth, and
 * ClerkPublicAuthBridge writes the real answer back once Clerk has loaded, so a
 * stale hint (expired session, sign-out in another tab) is corrected on the next
 * Clerk-backed render at the cost of one unnecessary Clerk boot.
 *
 * localStorage, not sessionStorage: the signal has to reach a brand-new tab,
 * which is how the app footer's legal links open the public pages.
 */
export const readSignedInHint = (): boolean => {
  try {
    return window.localStorage.getItem(SIGNED_IN_HINT_STORAGE_KEY) === '1';
  } catch {
    // Storage can be missing or blocked (private windows, blocked site data).
    // No hint means the Clerk-free public page, which is the safe direction.
    return false;
  }
};

export const writeSignedInHint = (signedIn: boolean): void => {
  try {
    if (signedIn) {
      window.localStorage.setItem(SIGNED_IN_HINT_STORAGE_KEY, '1');
    } else {
      window.localStorage.removeItem(SIGNED_IN_HINT_STORAGE_KEY);
      window.localStorage.removeItem(PROVIDER_NAME_HINT_STORAGE_KEY);
    }
  } catch {
    // Losing the hint only costs the signed-in public header on a cold load.
  }
};

/**
 * [MCV-1834] A provider's MCV profile name, kept for the PUBLIC header.
 *
 * Public pages render outside the app, so the provider record the vault loads is not in scope
 * there. The app writes the name here whenever it has the record; the public header reads it back
 * instead of the Clerk name. It is display-only, like the signed-in hint, and is only used for the
 * Clerk user it was written for (`clerkUserId`), so another account on the same browser never
 * sees it. It is cleared with the signed-in hint on sign-out.
 */
export interface ProviderNameHint {
  clerkUserId: string;
  firstName: string;
  lastName: string;
}

export const readProviderNameHint = (clerkUserId: string | undefined): ProviderNameHint | null => {
  if (!clerkUserId) return null;
  try {
    const raw = window.localStorage.getItem(PROVIDER_NAME_HINT_STORAGE_KEY);
    if (!raw) return null;
    const hint = JSON.parse(raw) as Partial<ProviderNameHint> | null;
    if (
      hint?.clerkUserId !== clerkUserId ||
      typeof hint.firstName !== 'string' ||
      typeof hint.lastName !== 'string'
    ) {
      return null;
    }
    return { clerkUserId, firstName: hint.firstName, lastName: hint.lastName };
  } catch {
    return null;
  }
};

export const writeProviderNameHint = (hint: ProviderNameHint): void => {
  try {
    window.localStorage.setItem(PROVIDER_NAME_HINT_STORAGE_KEY, JSON.stringify(hint));
  } catch {
    // Losing the name only means the public header falls back to the Clerk name.
  }
};
